import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Config from '../../../../../Config';
import type ProblemDomainSelectionBlockData from '../../../../../Models/Content/ProblemDomainSelectionBlockData';
import { replacePlaceholders } from '../../../../../utilities/StringHelpers/TitleHelpers';
import { iconBackChevron, iconSearch } from '../../images';
import { FAULT_TYPE, getChosenProductNameForReport, getMalfunctionTypeForReport, getProblemDomainGreetingName } from '../../problemDomainHelpers';
import type { DetectFaultResult } from '../../problemDomainHelpers';
import type { SubscriptionOption } from '../SubscriptionSelectionPage/SubscriptionSelectionPage';
import { reportDWH } from '../../problemDomainBlockSlice';
import '../DomainSelectionPage/domainSelectionPage.scss';
import './detectedFaultPage.scss';

type CmsImageRef = {
  expandedValue?: { url?: string };
  value?: { url?: string };
};

type CmsPageRef = {
  expandedValue?: { url?: string };
  value?: string | { url?: string };
};

type Props = {
  iContent: ProblemDomainSelectionBlockData;
  selectedSubscription: SubscriptionOption;
  detectedFaultResult?: DetectFaultResult | null;
  onBack: () => void;
};

const mediaUrl = (imageProp: CmsImageRef | undefined): string | null => {
  const path = imageProp?.expandedValue?.url || imageProp?.value?.url;
  return path ? Config.epiBaseUrl + path : null;
};

const pageUrl = (pageRef: CmsPageRef | undefined): string | null => {
  if (!pageRef) {
    return null;
  }
  const raw =
    pageRef.expandedValue?.url ||
    (typeof pageRef.value === 'object' ? pageRef.value?.url : pageRef.value);
  if (!raw || typeof raw !== 'string') {
    return null;
  }
  if (raw.startsWith('http') || raw.startsWith('/')) {
    return raw;
  }
  return Config.epiBaseUrl + raw;
};

const DetectedFaultPage = ({
  iContent,
  selectedSubscription,
  detectedFaultResult,
  onBack,
}: Props): React.ReactElement => {
  const dispatch = useDispatch();
  const selectedWorld = useSelector(
    (state: { problemDomainBlock?: { selectedWorld?: number | null } }) =>
      state.problemDomainBlock?.selectedWorld,
  );
  const loginData = useSelector(
    (state: {
      authentication?: {
        loginData?: {
          userDetails?: {
            firstName?: string;
            contactNumber?: string;
          };
        };
      };
    }) => state.authentication?.loginData,
  );

  const isPowerSupplyFault = detectedFaultResult?.type === FAULT_TYPE.powerSupply;

  useEffect(() => {
    dispatch(reportDWH({
      actionType: 'MalfunctionDetectedPage',
      status: 'Ongoing',
      messageText: 'Known Malfunction Page Displayed',
      param1: getChosenProductNameForReport(selectedWorld),
      param2: getMalfunctionTypeForReport(detectedFaultResult?.type),
    }));
  }, [dispatch, selectedWorld, detectedFaultResult?.type]);

  const titleTemplate = isPowerSupplyFault
    ? iContent?.detectedTransformerFaultTitle?.value
    : iContent?.detectedRegionalFaultTitle?.value;
  const explanationHtml = isPowerSupplyFault
    ? iContent?.detectedTransformerFaultExplanation?.value
    : iContent?.detectedRegionalFaultExplanation?.value;
  const buttonText = isPowerSupplyFault
    ? iContent?.detectedTransformerFaultButtonText?.value
    : iContent?.detectedRegionalFaultButtonText?.value;
  const buttonHref = pageUrl(
    (isPowerSupplyFault
      ? iContent?.detectedTransformerFaultButtonPage
      : iContent?.detectedRegionalFaultButtonPage) as unknown as CmsPageRef,
  );

  const title = useMemo(() => {
    if (!titleTemplate) {
      return '';
    }

    const greetingName = getProblemDomainGreetingName(
      loginData?.userDetails,
      selectedSubscription?.raw,
    );
    const firstName =
      String(loginData?.userDetails?.firstName ?? '').trim() ||
      String(selectedSubscription?.raw?.productFirstName ?? '').trim();
    const faultTitle = replacePlaceholders(titleTemplate, { firstName });
    const greeting = greetingName ? `היי ${greetingName},` : 'היי,';

    return `${greeting}<br />${faultTitle}`;
  }, [loginData, selectedSubscription, titleTemplate]);

  const bgDesktop = mediaUrl(iContent?.backgroundImageDesktop as unknown as CmsImageRef);
  const bgMobile = mediaUrl(iContent?.backgroundImageMobile as unknown as CmsImageRef);

  const sectionStyle = {
    ...(bgDesktop ? { '--prob-domain-bg-desktop': `url("${bgDesktop}")` } : {}),
    ...(bgMobile ? { '--prob-domain-bg-mobile': `url("${bgMobile}")` } : {}),
  } as React.CSSProperties;

  const handleButtonClick = () => {
    dispatch(reportDWH({
      actionType: 'MalfunctionDetectedPage',
      status: 'Completed',
      messageText: 'Wizard Button Clicked',
      param1: getChosenProductNameForReport(selectedWorld),
      param2: getMalfunctionTypeForReport(detectedFaultResult?.type),
    }));

    if (buttonHref) {
      window.location.href = buttonHref;
    }
  };

  return (
    <section className="problem-domain-selection problem-domain-detected-fault" style={sectionStyle}>
      <header className="problem-domain-selection__top">
        <button type="button" className="problem-domain-selection__back" onClick={onBack}>
          <span className="problem-domain-selection__back-content">
            <img
              src={iconBackChevron}
              alt=""
              width={16}
              height={16}
              className="problem-domain-selection__back-icon"
              aria-hidden
            />
            <span>חזרה</span>
          </span>
        </button>
      </header>

      <div className="problem-domain-detected-fault__shell">
        {title ? (
          <h1
            className="problem-domain-detected-fault__title"
            dangerouslySetInnerHTML={{ __html: title }}
          />
        ) : null}

        {explanationHtml ? (
          <div className="problem-domain-detected-fault__card">
            <img
              src={iconSearch}
              width={22}
              height={22}
              className="problem-domain-detected-fault__search-icon"
              alt=""
              aria-hidden
            />
            <div
              className="problem-domain-detected-fault__explanation"
              dangerouslySetInnerHTML={{ __html: explanationHtml }}
            />
          </div>
        ) : null}

        {buttonText ? (
          <button
            type="button"
            className="problem-domain-detected-fault__button"
            onClick={handleButtonClick}
            disabled={!buttonHref}
          >
            {buttonText}
          </button>
        ) : null}
      </div>
    </section>
  );
};

export default DetectedFaultPage;
